import { useEffect, useRef, useState } from 'react'
import { useRxDB } from './useRxDB'
import {
  createEntity,
  deleteAuditFields,
  updateAuditFields,
} from './utils'
import type {
  BaseDocument,
  EntityMutations,
  UseEntityResult,
} from './types'
import useLoginStore from '@/store/LoginStore'
import { logger } from '@/lib/monitoring/logger'

/**
 * Generic hook factory for creating entity CRUD hooks
 */
export const createEntityHooks = <T extends BaseDocument>(
  collectionName: string
) => {
  /**
   * Hook to query multiple entities with filters.
   * Pass `{ enabled: false }` to skip the subscription (e.g. when a required
   * id isn't available yet). `undefined` filter means "no filter" — subscribe
   * to all non-deleted rows.
   */
  const useEntities = (
    filter?: Partial<T> & { isDeleted?: boolean },
    options?: { enabled?: boolean },
  ) => {
    const { db, isInitializing } = useRxDB()
    const [data, setData] = useState<Array<T>>([])
    const [isLoading, setIsLoading] = useState(true)
    // Tracks which filter key the current `data` was emitted for. Used to
    // synchronously suppress stale data when the filter changes mid-render,
    // before the effect has had a chance to clear/re-subscribe.
    const [dataFilterKey, setDataFilterKey] = useState<string>('')
    const enabled = options?.enabled ?? true

    // Use ref to track the serialized filter and only update when it actually changes
    const filterRef = useRef<string>('')
    const currentFilterStr = enabled ? JSON.stringify(filter ?? {}) : '__disabled__'
    if (filterRef.current !== currentFilterStr) {
      filterRef.current = currentFilterStr
    }
    const stableFilterKey = filterRef.current

    useEffect(() => {
      // Clear stale results from the previous filter so consumers don't
      // briefly render the previous query's data under the new filter key.
      setData([])
      setIsLoading(true)

      if (!enabled) {
        setDataFilterKey(stableFilterKey)
        setIsLoading(false)
        return
      }

      if (!db) {
        // No subscription will run, but mark this filter as the current one
        // so consumers don't stay stuck behind the stale-data guard.
        setDataFilterKey(stableFilterKey)
        setIsLoading(isInitializing)
        return
      }

      let subscription: any

      const subscribe = async () => {
        try {
          const selector: any = {
            isDeleted: filter?.isDeleted ?? false,
          }

          if (filter) {
            Object.entries(filter).forEach(([key, value]) => {
              if (key !== 'isDeleted' && value !== undefined) {
                selector[key] = value
              }
            })
          }

          const query = (db as any)[collectionName].find({ selector })

          subscription = query.$.subscribe({
            next: (docs: Array<any>) => {
              setData(docs.map((doc: any) => doc.toJSON()))
              setDataFilterKey(stableFilterKey)
              setIsLoading(false)
            },
            error: (err: Error) => {
              logger.logError(err, 'DATABASE', {
                collection: collectionName,
                operation: 'query_subscription',
              })
              setIsLoading(false)
            },
          })
        } catch (err) {
          logger.logError(err as Error, 'DATABASE', {
            collection: collectionName,
            operation: 'subscribe_to_query',
          })
          setIsLoading(false)
        }
      }

      subscribe()

      return () => {
        if (subscription) subscription.unsubscribe()
      }
    }, [db, isInitializing, stableFilterKey])

    // If the filter has changed but the new query hasn't emitted yet, the
    // `data` state still belongs to the previous filter — treat it as empty
    // and still-loading so consumers don't render stale rows for one frame.
    const isFresh = dataFilterKey === stableFilterKey
    return {
      data: isFresh ? data : [],
      isLoading: isFresh ? isLoading : true,
      db,
    }
  }

  /**
   * Hook to get a single entity by ID
   */
  const useEntity = (id: string | undefined): UseEntityResult<T> => {
    const { db } = useRxDB()
    const [data, setData] = useState<T | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
      if (!db || !id) {
        setIsLoading(false)
        return
      }

      let subscription: any

      const subscribe = async () => {
        try {
          subscription = (db as any)[collectionName]
            .findOne(id)
            .$.subscribe({
              next: (doc: any) => {
                // Filter out soft-deleted entities
                const jsonDoc = doc ? doc.toJSON() : null
                setData(jsonDoc && !jsonDoc.isDeleted ? jsonDoc : null)
                setIsLoading(false)
              },
              error: (err: Error) => {
                logger.logError(err, 'DATABASE', {
                  collection: collectionName,
                  entityId: id,
                  operation: 'entity_subscription',
                })
                setIsLoading(false)
              },
            })
        } catch (err) {
          logger.logError(err as Error, 'DATABASE', {
            collection: collectionName,
            entityId: id,
            operation: 'subscribe_to_entity',
          })
          setIsLoading(false)
        }
      }

      subscribe()

      return () => {
        if (subscription) subscription.unsubscribe()
      }
    }, [db, id])

    return { data, isLoading }
  }

  /**
   * Hook for entity mutations (create, update, delete)
   */
  const useMutations = (): EntityMutations<T> => {
    const { db } = useRxDB()
    const userId = useLoginStore((state) => state.userId)

    const create = async (data: Partial<T>): Promise<T> => {
      if (!db || !userId) {
        throw new Error('Database not initialized or user not logged in')
      }

      const entity = createEntity(data, userId)

      try {
        const insertPromise = (db as any)[collectionName].insert(entity)

        await Promise.race([
          insertPromise,
          new Promise((_, reject) =>
            setTimeout(() => {
              reject(new Error(`Insert timeout after 10s for ${collectionName}`))
            }, 10000)
          )
        ])

        return entity as T
      } catch (insertError) {
        logger.logError(insertError as Error, 'DATABASE', {
          collection: collectionName,
          operation: 'create',
        })
        throw insertError
      }
    }

    const update = async (id: string, updates: Partial<T>): Promise<void> => {
      if (!db || !userId) {
        throw new Error('Database not initialized or user not logged in')
      }

      const maxRetries = 3
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          const doc = await (db as any)[collectionName].findOne(id).exec()
          if (!doc) {
            throw new Error(`${collectionName} ${id} not found`)
          }

          await doc.update({
            $set: {
              ...updates,
              ...updateAuditFields(userId),
            },
          })
          return
        } catch (updateError: any) {
          // Retry on conflict (stale revision) — another update landed first
          if (updateError?.code === 'CONFLICT' && attempt < maxRetries - 1) {
            continue
          }
          logger.logError(updateError as Error, 'DATABASE', {
            collection: collectionName,
            entityId: id,
            operation: 'update',
          })
          throw updateError
        }
      }
    }

    const deleteEntity = async (id: string): Promise<void> => {
      if (!db || !userId) {
        throw new Error('Database not initialized or user not logged in')
      }

      try {
        const doc = await (db as any)[collectionName].findOne(id).exec()
        if (!doc) {
          throw new Error(`${collectionName} ${id} not found`)
        }

        // Soft delete
        await doc.update({
          $set: deleteAuditFields(userId),
        })
      } catch (deleteError) {
        logger.logError(deleteError as Error, 'DATABASE', {
          collection: collectionName,
          entityId: id,
          operation: 'delete',
        })
        throw deleteError
      }
    }

    return {
      create,
      update,
      delete: deleteEntity,
    }
  }

  return {
    useEntities,
    useEntity,
    useMutations,
  }
}
